import { forwardRef, Fragment } from "react";
import Box from "@mui/material/Box";
import classes from "./PrintInvoiceContent.module.css";
import Typography from "@mui/material/Typography";
import { DateField, NumberField, RecordContextProvider } from "react-admin";
import splitArrayToChunks from "../../../../utils/splitArrayToChunks";

const PrintInvoiceContent = forwardRef((props, ref) => {
  // Validate record has required data
  if (!props.record) {
    return <div>No data available</div>;
  }

  // Transform items for sale order (different from sales)
  const items = props.record.items || [];

  // For sale order, we need to transform items to match component expectations
  const transformedItems = items.map(item => ({
    product_code: item.product_code,
    product_name: item.product_name,
    quantity_jual: item.quantity, // Sale order only has ordered quantity
    quantity_return: 0, // Sale order doesn't have returns yet
    quantity_net: item.quantity, // Net = ordered quantity
    sale_price_net: item.price,
    subtotal_price_net: item.subtotal
  }));

  return (
    <Box
      component="div"
      sx={{
        display: "none",
      }}
    >
      <Box ref={ref}>
        <style type="text/css" media="print">
          {`@page {
              width: 215.9mm;
              height: 139.7mm;
              size: 8.5in 5.5in portrait;
              margin: 0;
            }
            html, body {
              width: 215.9mm;
              height: 100%;
            }
            .page-break {
              display: block;
              page-break-after: always;
            }
            .big-back-inv::before {
              content: "";
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-image: url('/atena-tiles.png');
              background-repeat: no-repeat;
              background-position: 50% 28%;
              background-size: 20% auto;
              opacity: 0.2;
              z-index: -1;
            }
          `}
        </style>
          {splitArrayToChunks(transformedItems, 5).map((arr, i, ori) => {
          return (
            <Fragment key={i}>
              <Box
                className="big-back-inv"
                sx={{
                  fontSize: "12px",
                  py: 2,
                  px: 2,
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  height: "calc(100vh - 40px)",
                  mt: i > 0 ? 2 : 0,
                  position: "relative",
                }}
              >
                <Box>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        mb: 0.5,
                        fontFamily: "Arial, Helvetica, sans-serif",
                      }}
                    >
                      {props.record.company_name}
                    </Typography>
                  </div>
                  <Box
                    component="table"
                    className={classes.headerSj}
                    sx={{
                      "& th.no-wrap": {
                        whiteSpace: "nowrap",
                      },
                      "& th": {
                        verticalAlign: "top",
                      },
                    }}
                  >
                    <thead>
                      <tr>
                        <th>Kepada Yth</th>
                        <th>:</th>
                        <th style={{ fontWeight: "normal" }}>
                          {props.record.customer_name}
                        </th>
                        <th style={{ width: "5%" }}></th>
                        <th className="no-wrap">No. Order</th>
                        <th>:</th>
                        <th className="no-wrap" style={{ fontWeight: "normal" }}>
                          {props.record.code}
                        </th>
                      </tr>
                      <tr>
                        <th>No. Customer</th>
                        <th>:</th>
                        <th style={{ fontWeight: "normal" }}>
                          {props.record.customer_code}
                        </th>
                        <th style={{ width: "5%" }}></th>
                        <th className="no-wrap">Tgl. Order</th>
                        <th>:</th>
                        <th className="no-wrap" style={{ fontWeight: "normal" }}>
                          <RecordContextProvider value={props.record}>
                            <DateField
                              source="date"
                              sx={{
                                fontFamily: "Arial, Helvetica, sans-serif",
                                fontWeight: "normal",
                                fontSize: "12px",
                              }}
                              options={{
                                year: "numeric",
                                month: "short",
                                day: "2-digit",
                              }}
                            />
                          </RecordContextProvider>
                        </th>
                      </tr>
                      <tr>
                        <th rowSpan={3}>Alamat</th>
                        <th rowSpan={3}>:</th>
                        <th rowSpan={3} style={{ fontWeight: "normal" }}>
                          {props.record.customer_address}
                        </th>
                        <th rowSpan={3} style={{ width: "5%" }}></th>
                        <th className="no-wrap">Nama Sales</th>
                        <th>:</th>
                        <th style={{ fontWeight: "normal" }}>
                          {props.record.salesman_name}
                        </th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                      <tr>
                        <th className="no-wrap">Contact Person</th>
                        <th>:</th>
                        <th style={{ fontWeight: "normal" }}>
                          {props.record.customer_pic}
                        </th>
                        <th style={{ width: "5%" }}></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                      <tr>
                        <th className="no-wrap">Telp / Fax</th>
                        <th>:</th>
                        <th style={{ fontWeight: "normal" }}>
                          {props.record.customer_phone}
                        </th>
                        <th style={{ width: "5%" }}></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                  </Box>
                  <Typography
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "18px",
                      mt: 0,
                      mb: 0.5,
                      lineHeight: 1,
                      fontFamily: "Arial, Helvetica, sans-serif",
                    }}
                  >
                    INVOICE SALE ORDER
                  </Typography>
                </Box>
                <Box sx={{ height: "100%", flex: 1 }}>
                  <Box
                    component="table"
                    className={`${classes.tableSj} ${classes.invoiceItems}`}
                    sx={{
                      width: "100%",
                      "& th.no-wrap": {
                        whiteSpace: "nowrap",
                      },
                      "& th": {
                        verticalAlign: "top",
                      },
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            width: "1%",
                            fontSize: "11px",
                            textAlign: "right",
                          }}
                        >
                          No
                        </th>
                        <th style={{ fontSize: "11px" }}>Kode Barang</th>
                        <th style={{ fontSize: "11px" }}>Nama Barang</th>
                        <th
                          style={{
                            width: "10%",
                            fontSize: "11px",
                            textAlign: "right",
                          }}
                        >
                          Q Order (Box)
                        </th>
                        <th
                          style={{
                            width: "10%",
                            fontSize: "11px",
                            textAlign: "right",
                          }}
                        >
                          Q Retur (Box)
                        </th>
                        <th
                          style={{
                            width: "10%",
                            fontSize: "11px",
                            textAlign: "right",
                          }}
                        >
                          Q Net (Box)
                        </th>
                        <th
                          colSpan={2}
                          style={{ fontSize: "11px", textAlign: "right" }}
                        >
                          Harga (Rp)
                        </th>
                        <th style={{ fontSize: "11px", textAlign: "right" }}>
                          Total (Rp)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {arr.map((obj, idx) => {
                        return (
                          <tr key={obj.product_code}>
                            <td
                              style={{
                                textAlign: "right",
                                borderTop: "none",
                                borderBottom: "none",
                                paddingTop: "0",
                                paddingBottom: "0",
                                height: "1px",
                                margin: 0,
                                fontSize: "10px",
                              }}
                            >
                              {idx + 1}
                            </td>
                            <td
                              className={classes.noWrap}
                              style={{
                                borderTop: "none",
                                borderBottom: "none",
                                paddingTop: "0",
                                paddingBottom: "0",
                                height: "1px",
                                margin: 0,
                                fontSize: "10px",
                              }}
                            >
                              {obj.product_code}
                            </td>
                            <td
                              style={{
                                borderTop: "none",
                                borderBottom: "none",
                                paddingTop: "0",
                                paddingBottom: "0",
                                height: "1px",
                                margin: 0,
                                fontSize: "10px",
                              }}
                            >
                              <p
                                style={{
                                  margin: 0,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  textOverflow: "ellipsis",
                                  overflow: "hidden",
                                }}
                              >
                                {obj.product_name}
                              </p>
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                borderTop: "none",
                                borderBottom: "none",
                                paddingTop: "0",
                                paddingBottom: "0",
                                height: "1px",
                                margin: 0,
                              }}
                            >
                              <RecordContextProvider value={obj}>
                                <NumberField
                                  source="quantity_jual"
                                  sx={{
                                    fontFamily: "Arial, Helvetica, sans-serif",
                                    fontWeight: "inherit",
                                    fontSize: "10px",
                                  }}
                                ></NumberField>
                              </RecordContextProvider>
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                borderTop: "none",
                                borderBottom: "none",
                                paddingTop: "0",
                                paddingBottom: "0",
                                height: "1px",
                                margin: 0,
                                fontSize: "10px",
                              }}
                            >
                              <RecordContextProvider value={obj}>
                                <NumberField
                                  source="quantity_return"
                                  sx={{
                                    fontFamily: "Arial, Helvetica, sans-serif",
                                    fontWeight: "inherit",
                                    fontSize: "12px",
                                  }}
                                ></NumberField>
                              </RecordContextProvider>
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                borderTop: "none",
                                borderBottom: "none",
                                paddingTop: "0",
                                paddingBottom: "0",
                                height: "1px",
                                margin: 0,
                                fontSize: "10px",
                              }}
                            >
                              <RecordContextProvider value={obj}>
                                <NumberField
                                  source="quantity_net"
                                  sx={{
                                    fontFamily: "Arial, Helvetica, sans-serif",
                                    fontWeight: "inherit",
                                    fontSize: "12px",
                                  }}
                                ></NumberField>
                              </RecordContextProvider>
                            </td>
                            <td
                              colSpan={2}
                              style={{
                                textAlign: "right",
                                borderTop: "none",
                                borderBottom: "none",
                                paddingTop: "0",
                                paddingBottom: "0",
                                height: "1px",
                                margin: 0,
                                fontSize: "10px",
                              }}
                            >
                              <RecordContextProvider value={obj}>
                                <NumberField
                                  source="sale_price_net"
                                  sx={{
                                    fontFamily: "Arial, Helvetica, sans-serif",
                                    fontWeight: "inherit",
                                    fontSize: "12px",
                                  }}
                                ></NumberField>
                              </RecordContextProvider>
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                borderTop: "none",
                                borderBottom: "none",
                                paddingTop: "0",
                                paddingBottom: "0",
                                height: "1px",
                                margin: 0,
                                fontSize: "10px",
                              }}
                            >
                              <RecordContextProvider value={obj}>
                                <NumberField
                                  source="subtotal_price_net"
                                  sx={{
                                    fontFamily: "Arial, Helvetica, sans-serif",
                                    fontWeight: "inherit",
                                    fontSize: "12px",
                                  }}
                                ></NumberField>
                              </RecordContextProvider>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            textAlign: "right",
                            verticalAlign: "baseline",
                            fontWeight: "bold",
                            borderLeft: "none",
                            borderRight: "none",
                            borderBottom: "none",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        >
                          Total Kuantitas :
                        </td>
                        <td
                          style={{
                            textAlign: "right",
                            verticalAlign: "baseline",
                            fontWeight: "bold",
                            borderLeft: "none",
                            borderRight: "none",
                            borderBottom: "none",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        >
                          <RecordContextProvider value={props.record}>
                            <NumberField
                              source="total_quantity"
                              sx={{
                                fontFamily: "Arial, Helvetica, sans-serif",
                                fontWeight: "inherit",
                                fontSize: "12px",
                              }}
                            ></NumberField>
                          </RecordContextProvider>
                        </td>
                        <td
                          style={{
                            verticalAlign: "baseline",
                            fontWeight: "bold",
                            borderLeft: "none",
                            borderRight: "none",
                            borderBottom: "none",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        >
                          Sub Total
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            fontWeight: "bold",
                            borderLeft: "none",
                            borderRight: "none",
                            borderBottom: "none",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        >
                          :
                        </td>
                        <td
                          style={{
                            textAlign: "right",
                            verticalAlign: "baseline",
                            fontWeight: "bold",
                            borderLeft: "none",
                            borderRight: "none",
                            borderBottom: "none",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        >
                          <RecordContextProvider value={props.record}>
                            <NumberField
                              source="total"
                              sx={{
                                fontFamily: "Arial, Helvetica, sans-serif",
                                fontWeight: "inherit",
                                fontSize: "12px",
                              }}
                            ></NumberField>
                          </RecordContextProvider>
                        </td>
                      </tr>
                      {/* Remove pre-received and cash discount for sale order */}
                      <tr>
                        <td
                          colSpan={6}
                          style={{
                            border: "none",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        ></td>
                        <td
                          style={{
                            verticalAlign: "baseline",
                            fontWeight: "bold",
                            borderBottom: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        >
                          Total
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "baseline",
                            fontWeight: "bold",
                            borderBottom: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        >
                          :
                        </td>
                        <td
                          style={{
                            textAlign: "right",
                            verticalAlign: "baseline",
                            fontWeight: "bold",
                            borderBottom: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        >
                          <RecordContextProvider value={props.record}>
                            <NumberField
                              source="total"
                              sx={{
                                fontFamily: "Arial, Helvetica, sans-serif",
                                fontWeight: "inherit",
                                fontSize: "12px",
                              }}
                            ></NumberField>
                          </RecordContextProvider>
                        </td>
                      </tr>
                    </tfoot>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    mt: "auto",
                    pb: 2,
                  }}
                >
                  {/* Header */}
                  <Box sx={{ display: "flex", mb: 1 }}>
                    <Box sx={{ width: "60%", textAlign: "center", fontWeight: "bold", fontSize: "11px" }}>
                      Pelaksana
                    </Box>
                    <Box sx={{ width: "40%", textAlign: "center", fontWeight: "bold", fontSize: "11px" }}>
                      Diterima Oleh
                    </Box>
                  </Box>

                  {/* Tanda Tangan */}
                  <Box sx={{ display: "flex", height: "100px", alignItems: "flex-end", mt: 3 }}>
                    <Box sx={{ width: "60%", display: "flex", justifyContent: "space-around" }}>
                      {/* SPV Sales */}
                      <Box sx={{ width: "48%", textAlign: "center" }}>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            letterSpacing: "2px",
                            fontFamily: "Arial, Helvetica, sans-serif",
                            color: "#000",
                            mb: 0.5,
                            minHeight: "24px",
                            borderBottom: "1px dashed #999",
                            width: "90%",
                            mx: "auto",
                          }}
                        >
                          ( ................... )
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "9px", color: "#666", mt: 0.5 }}
                        >
                          SPV Sales
                        </Typography>
                      </Box>

                      {/* Sales */}
                      <Box sx={{ width: "48%", textAlign: "center" }}>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            letterSpacing: "2px",
                            fontFamily: "Arial, Helvetica, sans-serif",
                            color: "#000",
                            mb: 0.5,
                            minHeight: "24px",
                            borderBottom: "1px dashed #999",
                            width: "90%",
                            mx: "auto",
                          }}
                        >
                          ( ................... )
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "9px", color: "#666", mt: 0.5 }}
                        >
                          Sales
                        </Typography>
                      </Box>
                    </Box>

                    {/* Toko */}
                    <Box sx={{ width: "40%", textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          letterSpacing: "2px",
                          fontFamily: "Arial, Helvetica, sans-serif",
                          color: "#000",
                          mb: 0.5,
                          minHeight: "24px",
                          borderBottom: "1px dashed #999",
                          width: "90%",
                          mx: "auto",
                        }}
                      >
                        ( ................... )
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "9px", color: "#666", mt: 0.5 }}
                      >
                        Toko
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "10px",
                    fontFamily: "Arial, Helvetica, sans-serif",
                  }}
                >
                  Dibuat Oleh: {props.record.created_by}, {props.record.created_at}
                </Typography>
              </Box>
              <div className="page-break" />
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
});

export default PrintInvoiceContent;
